import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Colors } from 'context';
import { CareerIcon } from 'atoms';
import { careerLayout } from 'services/careerType';

export interface ICareerLinkCard {
  /** Адрес внутри приложения. Строится из `PATHS` или готовым с сервера. */
  to?: string;
  /** Внешний адрес — открывается в новой вкладке. Взаимоисключим с `to`. */
  href?: string;
  children: ReactNode;
  /**
   * `current` — текущее действие или этап: жёлтая рамка. Одна такая карточка
   * на экран — иначе «текущее» перестаёт что-либо значить.
   */
  tone?: 'default' | 'current' | 'quiet';
  /** Стрелка перехода справа. По умолчанию есть. */
  withArrow?: boolean;
  /** `compact` — узкая строка шага: паддинг 8/16, высота от 48 (доработка 15.09). */
  size?: 'regular' | 'compact';
  className?: string;
  'data-testid'?: string;
}

/**
 * Карточка с ОДНИМ назначением — нажимается целиком (ТЗ 0071, п. 2.4).
 *
 * Проверка в Brave: у похожих карточек были разные зоны нажатия — где-то
 * кликался только заголовок, где-то вся площадь через растянутый псевдоэлемент
 * поверх `<button>`. Кнопка не открывается в новой вкладке, не копирует адрес и
 * не показывает его при наведении; ссылка делает всё это сама.
 *
 * Поэтому вся карточка — ОДНА семантическая ссылка: фон, иконка, текст и
 * отступы внутри неё. Enter открывает, Cmd/Ctrl+клик — новая вкладка.
 *
 * 🔴 Внутрь НЕЛЬЗЯ класть кнопки и другие ссылки: вложенный интерактивный
 * элемент внутри `<a>` — невалидная разметка, и клик по нему уходит в обе
 * стороны. Карточке с несколькими действиями нужны отдельные кнопки, а не эта
 * обёртка. Сторож — тест в `__tests__/CareerLinkCard.test.tsx`.
 *
 * Информационный блок без перехода этой обёрткой не оборачивается: карточка,
 * выглядящая нажимаемой и не нажимающаяся, — та же проблема с другой стороны.
 */
export const CareerLinkCard = ({
  to,
  href,
  children,
  tone = 'default',
  withArrow = true,
  size = 'regular',
  className,
  'data-testid': testId,
}: ICareerLinkCard) => {
  const body = (
    <>
      <Content>{children}</Content>
      {withArrow ? (
        <Arrow>
          <CareerIcon name={href ? 'externalLink' : 'arrowRight'} size={20} />
        </Arrow>
      ) : null}
    </>
  );

  if (href) {
    return (
      <ExternalCard
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        $tone={tone}
        $size={size}
        className={className}
        data-testid={testId}>
        {body}
      </ExternalCard>
    );
  }
  return (
    <InternalCard
      to={to || '#'}
      $tone={tone}
      $size={size}
      className={className}
      data-testid={testId}>
      {body}
    </InternalCard>
  );
};

const cardStyles = css<{ $tone: 'default' | 'current' | 'quiet'; $size: 'regular' | 'compact' }>`
  display: flex;
  align-items: center;
  gap: 16px;
  box-sizing: border-box;
  width: 100%;
  min-height: ${({ $size }) =>
    $size === 'compact' ? careerLayout.primaryButton : careerLayout.hitTarget}px;
  padding: ${({ $size }) =>
    $size === 'compact' ? '6px 16px 6px 8px' : `${careerLayout.cardPadding}px`};
  border: ${({ $tone }) => ($tone === 'current' ? 2 : 1)}px solid
    ${({ $tone }) => ($tone === 'current' ? Colors.main : Colors.system_gray)};
  border-radius: ${careerLayout.cardRadius}px;
  background-color: ${({ $tone }) => ($tone === 'quiet' ? Colors.bg_gray : Colors.white)};
  color: ${Colors.black};
  text-decoration: none;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    color: ${Colors.black};
    border-color: ${({ $tone }) => ($tone === 'current' ? Colors.main_dark : Colors.black)};
    box-shadow: 0 4px 16px rgba(7, 7, 7, 0.08);
  }

  /* Кольцо на ВСЕЙ карточке: видимый фокус совпадает с зоной нажатия. Чёрное,
     а не жёлтое — жёлтое на белом даёт 1.33:1. */
  &:focus-visible {
    outline: 2px solid ${Colors.black};
    outline-offset: 2px;
  }
`;

const InternalCard = styled(Link)<{
  $tone: 'default' | 'current' | 'quiet';
  $size: 'regular' | 'compact';
}>`
  ${cardStyles}
`;

const ExternalCard = styled.a<{
  $tone: 'default' | 'current' | 'quiet';
  $size: 'regular' | 'compact';
}>`
  ${cardStyles}
`;

const Content = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  display: block;
`;

const Arrow = styled.span`
  flex: none;
  display: inline-flex;
  color: ${Colors.gray_dark};
`;
